import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config/api";
import loginBackground from "../assets/sfondi.png";

const AdminLogin = () => {
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const navigate = useNavigate();

  const handleChange = (e) => {
    setCredentials((previousCredentials) => ({
      ...previousCredentials,
      [e.target.name]: e.target.value,
    }));

    setError("");
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    setError("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
      });

      const text = await res.text();

      let data;

      try {
        data = JSON.parse(text);
      } catch {
        console.error("Server returned non-JSON:", text);
        setError("Server error");
        return;
      }

      if (!res.ok) {
        setError(data.message || "Login failed");
        return;
      }

      const user = data.user;
      const token = data.authToken;

      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("authToken", token);

      if (rememberMe) {
        localStorage.setItem(
          "rememberedUsername",
          credentials.username
        );
      } else {
        localStorage.removeItem("rememberedUsername");
      }

      if (user.role === "admin") {
        navigate("/admin/dashboard");
        return;
      }

      try {
        const permRes = await fetch(
          `${API_BASE_URL}/api/users/${user.id}/permissions`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!permRes.ok) {
          console.error("Could not load user permissions");
        }
      } catch (permissionError) {
        console.error(
          "Permission request failed:",
          permissionError
        );
      }

      navigate("/user/dashboard");
    } catch (err) {
      console.error(err);
      setError("Server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes loginCardEnter {
            from {
              opacity: 0;
              transform: translateY(35px) scale(0.97);
            }

            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          @keyframes loginCardFloat {
            0%,
            100% {
              transform: translateY(0);
            }

            50% {
              transform: translateY(-8px);
            }
          }

          @keyframes lockPulse {
            0%,
            100% {
              box-shadow:
                0 0 25px rgba(59, 130, 246, 0.5),
                0 0 45px rgba(168, 85, 247, 0.25);
            }

            50% {
              box-shadow:
                0 0 40px rgba(59, 130, 246, 0.8),
                0 0 70px rgba(168, 85, 247, 0.45);
            }
          }

          @keyframes buttonShine {
            0% {
              transform: translateX(-180%) rotate(20deg);
            }

            100% {
              transform: translateX(450%) rotate(20deg);
            }
          }

          @keyframes orbMoveOne {
            0%,
            100% {
              transform: translate(0, 0);
            }

            50% {
              transform: translate(40px, -35px);
            }
          }

          @keyframes orbMoveTwo {
            0%,
            100% {
              transform: translate(0, 0);
            }

            50% {
              transform: translate(-45px, 35px);
            }
          }

          .admin-login-card {
            animation:
              loginCardEnter 0.8s ease-out both,
              loginCardFloat 5s ease-in-out 0.8s infinite;
          }

          .admin-login-lock {
            animation: lockPulse 2.5s ease-in-out infinite;
          }

          .admin-login-button::before {
            content: "";
            position: absolute;
            top: -100%;
            left: -40%;
            width: 25%;
            height: 300%;
            background: rgba(255, 255, 255, 0.32);
            transform: rotate(20deg);
            animation: buttonShine 3.5s ease-in-out infinite;
          }

          .admin-login-orb-one {
            animation: orbMoveOne 8s ease-in-out infinite;
          }

          .admin-login-orb-two {
            animation: orbMoveTwo 9s ease-in-out infinite;
          }
        `}
      </style>

      <main
        className="
          relative
          flex
          min-h-[calc(100vh-80px)]
          items-center
          justify-center
          overflow-hidden
          px-4
          py-12
        "
      >
        {/* Background image */}
        <div
          className="
            absolute
            inset-0
            bg-cover
            bg-center
            bg-no-repeat
          "
          style={{
            backgroundImage: `url(${loginBackground})`,
          }}
        />

        {/* Overlay */}
        <div
          className="
            absolute
            inset-0
            bg-gradient-to-br
            from-[#020b33]/35
            via-[#11155d]/20
            to-[#4b087e]/35
          "
        />

        {/* Background glow */}
        <div
          className="
            admin-login-orb-one
            absolute
            -left-40
            top-20
            h-[420px]
            w-[420px]
            rounded-full
            bg-blue-500/20
            blur-[120px]
          "
        />

        <div
          className="
            admin-login-orb-two
            absolute
            -right-40
            bottom-0
            h-[470px]
            w-[470px]
            rounded-full
            bg-fuchsia-500/20
            blur-[130px]
          "
        />

        {/* Glass login card */}
        <section
          className="
            admin-login-card
            relative
            z-10
            w-full
            max-w-[480px]
            overflow-hidden
            rounded-[30px]
            border
            border-white/20
            bg-[#10175e]/55
            px-6
            py-8
            text-white
            shadow-[0_30px_100px_rgba(0,0,0,0.45)]
            backdrop-blur-2xl
            sm:px-10
            sm:py-10
          "
        >
          {/* Card internal glows */}
          <div
            className="
              pointer-events-none
              absolute
              -left-24
              -top-24
              h-64
              w-64
              rounded-full
              bg-blue-500/25
              blur-[85px]
            "
          />

          <div
            className="
              pointer-events-none
              absolute
              -bottom-28
              -right-24
              h-72
              w-72
              rounded-full
              bg-purple-500/25
              blur-[95px]
            "
          />

          <div className="relative z-10">
            {/* Lock icon */}
            <div className="mb-6 flex justify-center">
              <div
                className="
                  admin-login-lock
                  flex
                  h-[84px]
                  w-[84px]
                  items-center
                  justify-center
                  rounded-full
                  bg-gradient-to-br
                  from-blue-400
                  via-indigo-500
                  to-fuchsia-500
                  p-[3px]
                "
              >
                <div
                  className="
                    flex
                    h-full
                    w-full
                    items-center
                    justify-center
                    rounded-full
                    bg-[#151661]/90
                    backdrop-blur-xl
                  "
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-9 w-9 text-white"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect
                      x="5"
                      y="10"
                      width="14"
                      height="10"
                      rx="2"
                    />

                    <path d="M8 10V7a4 4 0 0 1 8 0v3" />

                    <path d="M12 14v2.5" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Heading */}
            <div className="mb-8 text-center">
              <h1
                className="
                  text-3xl
                  font-extrabold
                  tracking-tight
                  text-white
                  sm:text-4xl
                "
              >
                Master Sol&Tech
              </h1>

              <p className="mt-2 text-sm text-blue-100/75 sm:text-base">
                Admin &amp; Staff Login
              </p>
            </div>

            <form
              onSubmit={handleLogin}
              className="space-y-5"
            >
              {/* Username */}
              <div>
                <label
                  htmlFor="username"
                  className="
                    mb-2
                    block
                    text-sm
                    font-medium
                    text-blue-100
                  "
                >
                  Username
                </label>

                <div className="group relative">
                  <span
                    className="
                      pointer-events-none
                      absolute
                      inset-y-0
                      left-0
                      flex
                      items-center
                      pl-4
                      text-blue-100/70
                      transition
                      group-focus-within:text-blue-300
                    "
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                    </svg>
                  </span>

                  <input
                    id="username"
                    type="text"
                    name="username"
                    placeholder="Enter your username"
                    value={credentials.username}
                    onChange={handleChange}
                    autoComplete="username"
                    className="
                      w-full
                      rounded-xl
                      border
                      border-white/15
                      bg-white/10
                      py-3.5
                      pl-12
                      pr-4
                      text-white
                      outline-none
                      transition-all
                      duration-300
                      placeholder:text-blue-100/45
                      hover:border-blue-300/40
                      focus:border-blue-400
                      focus:bg-white/15
                      focus:ring-4
                      focus:ring-blue-500/20
                      focus:shadow-[0_0_25px_rgba(59,130,246,0.28)]
                    "
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label
                  htmlFor="password"
                  className="
                    mb-2
                    block
                    text-sm
                    font-medium
                    text-blue-100
                  "
                >
                  Password
                </label>

                <div className="group relative">
                  <span
                    className="
                      pointer-events-none
                      absolute
                      inset-y-0
                      left-0
                      flex
                      items-center
                      pl-4
                      text-blue-100/70
                      transition
                      group-focus-within:text-blue-300
                    "
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <rect
                        x="5"
                        y="10"
                        width="14"
                        height="10"
                        rx="2"
                      />

                      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
                    </svg>
                  </span>

                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Enter your password"
                    value={credentials.password}
                    onChange={handleChange}
                    autoComplete="current-password"
                    className="
                      w-full
                      rounded-xl
                      border
                      border-white/15
                      bg-white/10
                      py-3.5
                      pl-12
                      pr-12
                      text-white
                      outline-none
                      transition-all
                      duration-300
                      placeholder:text-blue-100/45
                      hover:border-blue-300/40
                      focus:border-blue-400
                      focus:bg-white/15
                      focus:ring-4
                      focus:ring-blue-500/20
                      focus:shadow-[0_0_25px_rgba(59,130,246,0.28)]
                    "
                    required
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword((previous) => !previous)
                    }
                    aria-label={
                      showPassword
                        ? "Hide password"
                        : "Show password"
                    }
                    className="
                      absolute
                      inset-y-0
                      right-0
                      flex
                      items-center
                      pr-4
                      text-blue-100/60
                      transition
                      hover:text-white
                    "
                  >
                    {showPassword ? (
                      <svg
                        viewBox="0 0 24 24"
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 3l18 18" />
                        <path d="M10.6 10.7a2 2 0 0 0 2.7 2.7" />
                        <path d="M9.9 4.2A10.6 10.6 0 0 1 12 4c5.5 0 9 5 9 5a16.5 16.5 0 0 1-3 3.6" />
                        <path d="M6.2 6.2C4.2 7.5 3 9 3 9s3.5 5 9 5c1 0 2-.2 2.8-.5" />
                      </svg>
                    ) : (
                      <svg
                        viewBox="0 0 24 24"
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 12s3.5-5 9-5 9 5 9 5-3.5 5-9 5-9-5-9-5Z" />
                        <circle cx="12" cy="12" r="2.5" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Remember me */}
              <div
                className="
                  flex
                  items-center
                  justify-between
                  gap-4
                "
              >
                <label
                  className="
                    flex
                    cursor-pointer
                    items-center
                    gap-2
                    text-sm
                    text-blue-100/85
                  "
                >
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) =>
                      setRememberMe(e.target.checked)
                    }
                    className="
                      h-4
                      w-4
                      cursor-pointer
                      rounded
                      accent-blue-500
                    "
                  />

                  Remember me
                </label>

                <button
                  type="button"
                  className="
                    text-sm
                    font-medium
                    text-blue-300
                    transition
                    hover:text-white
                  "
                >
                  Forgot password?
                </button>
              </div>

              {/* Error */}
              {error && (
                <div
                  className="
                    rounded-xl
                    border
                    border-red-400/30
                    bg-red-500/15
                    px-4
                    py-3
                    text-center
                    text-sm
                    text-red-100
                    backdrop-blur-sm
                  "
                >
                  {error}
                </div>
              )}

              {/* Login button */}
              <button
                type="submit"
                disabled={loading}
                className="
                  admin-login-button
                  relative
                  flex
                  w-full
                  items-center
                  justify-center
                  overflow-hidden
                  rounded-xl
                  bg-gradient-to-r
                  from-blue-500
                  via-indigo-500
                  to-fuchsia-500
                  px-5
                  py-3.5
                  font-bold
                  text-white
                  shadow-[0_15px_38px_rgba(79,70,229,0.38)]
                  transition-all
                  duration-300
                  hover:-translate-y-0.5
                  hover:shadow-[0_20px_50px_rgba(168,85,247,0.45)]
                  active:translate-y-0
                  disabled:cursor-not-allowed
                  disabled:opacity-70
                "
              >
                <span
                  className="
                    relative
                    z-10
                    flex
                    items-center
                    gap-2
                  "
                >
                  {loading && (
                    <span
                      className="
                        h-5
                        w-5
                        animate-spin
                        rounded-full
                        border-2
                        border-white/40
                        border-t-white
                      "
                    />
                  )}

                  {loading ? "Signing in..." : "Login"}
                </span>
              </button>
            </form>

            {/* Divider */}
            <div className="my-7 flex items-center gap-4">
              <div className="h-px flex-1 bg-white/15" />

              <span
                className="
                  text-[10px]
                  uppercase
                  tracking-[0.22em]
                  text-blue-100/45
                "
              >
                Secure access
              </span>

              <div className="h-px flex-1 bg-white/15" />
            </div>

            {/* Secure note */}
            <div
              className="
                flex
                items-center
                justify-center
                gap-2
                text-center
                text-xs
                text-blue-100/75
                sm:text-sm
              "
            >
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4 text-fuchsia-400"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 3 5 6v5c0 4.8 3 8.5 7 10 4-1.5 7-5.2 7-10V6l-7-3Z" />
                <path d="m9 12 2 2 4-4" />
              </svg>

              Secure access to MasterPhone dashboard
            </div>

            <p
              className="
                mt-7
                text-center
                text-xs
                text-blue-100/45
              "
            >
              © {new Date().getFullYear()} MasterPhone
            </p>
          </div>
        </section>
      </main>
    </>
  );
};

export default AdminLogin;